import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { CustomerDetailDrawerService } from 'app/modules/components/business-partners/customer-detail-drawer';
import { BusinessPartnerNavigationComponent } from 'app/shared/components/business-partner-navigation/business-partner-navigation.component';
import { MapModalComponent } from 'app/shared/components/map-modal/map-modal.component';
import {
  MapModalTypeEnum,
  MonitorDocumentTypeEnum,
} from 'app/shared/components/map-modal/models/map-modal.enum';
import { MapModalInputData } from 'app/shared/components/map-modal/models/map-modal.interface';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import { hasValidGeolocation } from '../../utils/has-valid-geolocation.util';

@Component({
  selector: 'app-partner-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    BusinessPartnerNavigationComponent,
    TranslatePipe,
  ],
  template: `
    <div class="partner-card" [class.selected]="selected()">
      <!-- Checkbox -->
      <mat-checkbox
        [checked]="selected()"
        (change)="onSelectionChange($event)"
        (click)="$event.stopPropagation()"
        class="flex-shrink-0"
      />

      <div class="partner-info">
        <!-- Code con Navigation + Routes Badge -->
        <div class="mb-1 flex items-center gap-2">
          <app-business-partner-navigation
            [businessPartner]="getBusinessPartnerForNavigation()"
            [displayMode]="'code'"
            [openInNewTab]="true"
            [showIcon]="true"
            [iconPosition]="'right'"
            [interactionMode]="'icon-only'"
            [showText]="true"
            [customClass]="'font-mono text-xs'"
          />
          @if (partner().subRoutes?.length > 0) {
            <span class="routes-badge">{{ 'routing.SUB_ROUTE_PARTNERS.CELLS.ROUTES_COUNT' | t: { count: partner().subRoutes.length } }}</span>
          }
        </div>

        <!-- Name -->
        <div class="partner-name">{{ partner().name }}</div>

        <!-- Commercial Name -->
        @if (partner().commercialName) {
          <div class="truncate text-xs text-gray-500">{{ partner().commercialName }}</div>
        }

        <!-- Address con Map Button -->
        <div class="partner-detail">
          <button
            mat-icon-button
            class="!h-5 !w-5 !min-w-0"
            [disabled]="!hasGeolocation()"
            (click)="openMapModal($event)"
            [matTooltip]="
              hasGeolocation()
                ? ('routing.SUB_ROUTE_PARTNERS.CELLS.ADDRESS_MAP_TOOLTIP' | t)
                : ('routing.SUB_ROUTE_PARTNERS.CELLS.ADDRESS_NO_GEO_TOOLTIP' | t)
            "
          >
            <mat-icon
              class="!text-sm"
              [class]="hasGeolocation() ? 'text-primary-500' : 'text-orange-500'"
              svgIcon="heroicons_solid:map-pin"
            ></mat-icon>
          </button>
          <span class="truncate">{{ partner().principalAddress?.address || '-' }}</span>
        </div>

        <!-- Location (ciudad, departamento) -->
        @if (getLocationText()) {
          <div class="partner-detail ml-5">
            <span class="text-xs text-gray-400">{{ getLocationText() }}</span>
          </div>
        }
      </div>

      <!-- Info Action -->
      <button
        mat-icon-button
        (click)="openCustomerDetail($event)"
        [matTooltip]="'routing.SUB_ROUTE_PARTNERS.CELLS.CLIENT_DETAIL_TOOLTIP' | t"
        class="!h-8 !w-8 flex-shrink-0"
      >
        <mat-icon class="!text-lg">info_outline</mat-icon>
      </button>

      <!-- Calendar Action -->
      <button
        mat-icon-button
        color="primary"
        (click)="onOpenCalendar($event)"
        [disabled]="disableIndividualCalendar()"
        [matTooltip]="
          disableIndividualCalendar()
            ? ('routing.SUB_ROUTE_PARTNERS.PARTNER_CARD.ASSIGN_FREQ_GROUP_TOOLTIP' | t)
            : ('routing.SUB_ROUTE_PARTNERS.PARTNER_CARD.ASSIGN_FREQ_TOOLTIP' | t)
        "
        class="!h-8 !w-8 flex-shrink-0"
      >
        <mat-icon class="!text-lg" svgIcon="heroicons_outline:calendar-days"></mat-icon>
      </button>
    </div>
  `,
  styles: `
    .partner-card {
      padding: 0.75rem;
      margin-bottom: 0.5rem;
      border-radius: 0.5rem;
      transition: all 0.2s ease-in-out;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      background: rgba(var(--fuse-bg-card-rgb), 1);
      border: 1px solid rgba(var(--fuse-border-rgb), 0.12);

      &:hover {
        background: rgba(var(--fuse-primary-rgb), 0.04);
        border-color: rgba(var(--fuse-primary-rgb), 0.2);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      &.selected {
        background: rgba(var(--fuse-primary-rgb), 0.12);
        border-left: 4px solid rgba(var(--fuse-primary-rgb), 1);
        border-color: rgba(var(--fuse-primary-rgb), 0.4);
      }
    }

    .partner-info {
      flex: 1;
      min-width: 0;
    }

    .partner-name {
      font-weight: 600;
      font-size: 0.875rem;
      color: rgba(var(--fuse-text-default-rgb), 1);
      margin-bottom: 0.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .partner-detail {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      color: rgba(var(--fuse-text-secondary-rgb), 1);
      margin-top: 0.125rem;

      mat-icon {
        color: rgba(var(--fuse-text-hint-rgb), 1);
        flex-shrink: 0;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .routes-badge {
      font-size: 0.625rem;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      background: rgba(var(--fuse-primary-rgb), 0.12);
      color: rgba(var(--fuse-primary-rgb), 1);
      font-weight: 600;
      white-space: nowrap;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerCardComponent {
  private readonly customerDrawer = inject(CustomerDetailDrawerService);
  private readonly dialog = inject(MatDialog);
  private readonly i18n = inject(LanguageService);

  partner = input.required<BusinessPartnerBySubRoute>();
  /**
   * Mongo `_id` of the sub-route's company. A sub-route belongs to exactly one
   * company, so the drawer must resolve the partner's per-company values
   * (`bpCompanies[]`) with it instead of the deprecated root copies.
   */
  companyId = input<string | null>(null);
  selected = input<boolean>(false);
  disableIndividualCalendar = input<boolean>(false);
  openCalendar = output<void>();
  selectionChange = output<boolean>();

  getBusinessPartnerForNavigation() {
    const p = this.partner();
    return {
      id: p._id,
      code: p.externalKeys?.code,
      name: p.name,
      externalKeys: p.externalKeys,
    };
  }

  hasGeolocation(): boolean {
    return hasValidGeolocation(this.partner().principalAddress?.geolocationPoint);
  }

  getLocationText(): string {
    const addr = this.partner().principalAddress;
    const parts = [addr?.locationThree?.name, addr?.locationTwo?.name].filter(Boolean);
    return parts.join(', ');
  }

  openMapModal(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.hasGeolocation()) return;

    const mapData: MapModalInputData = {
      type: MapModalTypeEnum.SIMPLE,
      tripId: null,
      element: this.partner(),
      document: MonitorDocumentTypeEnum.NONE,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: this.partner().name || this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.MAP_DEFAULT_TITLE') },
      },
    };

    this.dialog.open(MapModalComponent, {
      width: '90%',
      height: '80%',
      data: mapData,
    });
  }

  openCustomerDetail(event: MouseEvent): void {
    event.stopPropagation();
    this.customerDrawer.open(this.partner()._id, this.companyId() ?? undefined);
  }

  onOpenCalendar(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disableIndividualCalendar()) return;
    this.openCalendar.emit();
  }

  onSelectionChange(event: MatCheckboxChange): void {
    this.selectionChange.emit(event.checked);
  }
}
